import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import {
  API,
  IPayment,
  ICustomerWithHistory,
  CreateCustomer,
  IBaseLoyaltyTier,
  ICustomer,
} from '@getgreenline/homi-shared'
import { CustomerHistorySection } from './CustomerHistorySection'
import { CustomerPayments } from './CustomerPayments'
import { CustomerInfoForm } from './CustomerInfoForm'

import { parseErrorMsg } from '../../utilities/helpers'
import './index.scss'
import { isUndefined } from 'lodash'
import { Collapse, message } from 'antd-v4'
import { CustomerLoyaltyLogs } from './CustomerLoyaltyLogs'
import Endpoints from '../../constants/Endpoints'
import { ExternalLoyaltyAPI } from '@getgreenline/external-loyalty'
import { CustomerSearch } from '@getgreenline/customer-search'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  customerId?: string
  onSubmit: (customer: ICustomer | CustomerSearch.API.GlobalCustomer) => void
  onHide: () => void
  canSaveCustomer: boolean
  canViewCustomerTotalSpend: boolean
  customerLocationId?: number
  loadCustomers?: () => Promise<void>
  onCustomerUndelete?: () => void
}

interface State {
  isSaving: boolean
  errorMessage: string
  customerWithHistory: ICustomerWithHistory | undefined
  payments: IPayment[] | undefined
  failedToLoadExternalLoyaltyPoints: boolean
}

@inject('currentCompanyStore')
@observer
export class CustomerViewAndEditModal extends React.Component<Props, State> {
  customer = new CreateCustomer()
  canUseExternalLoyalty = this.props.currentCompanyStore!.canUseExternalLoyalty
  canAccessLoyalty = this.props.currentCompanyStore!.company?.permissions.canAccessLoyalty

  constructor(props: Props) {
    super(props)
    this.state = {
      isSaving: false,
      errorMessage: '',
      customerWithHistory: undefined,
      payments: undefined,
      failedToLoadExternalLoyaltyPoints: false,
    }
  }

  async componentDidMount() {
    const company = this.props.currentCompanyStore!.company!
    const { customerId } = this.props

    try {
      if (customerId) {
        const [customerWithHistory, payments] = await Promise.all([
          API.getCustomerById(company.id, customerId),
          this.props.currentCompanyStore!.getCustomerHistoricalPayments(customerId),
        ])
        const points = await this.loadExternalLoyaltyPoints(
          company.id,
          customerWithHistory.customer,
        )

        const customer = {
          ...customerWithHistory.customer,
          creditBalance:
            this.canUseExternalLoyalty && points !== undefined
              ? points
              : customerWithHistory.customer.creditBalance,
        }

        this.customer.populateFields(customer)

        this.setState({
          payments,
          failedToLoadExternalLoyaltyPoints: points === undefined,
          customerWithHistory: {
            ...customerWithHistory,
            customer,
          },
        })
      }

      this.props.currentCompanyStore!.getLoyaltyTiers()
    } catch (error) {
      this.setState({
        errorMessage: parseErrorMsg(error) || 'There was an error fetching customer data.',
      })
    }
  }

  loadExternalLoyaltyPoints = async (companyId: number, customer: ICustomer) => {
    try {
      if (this.canUseExternalLoyalty) {
        const { id, email, phone } = customer
        const points = await ExternalLoyaltyAPI.getExternalLoyaltyPoints({
          gatewayUrl: Endpoints.PLATFORM_GATEWAY_URL,
          merchantId: `blc-${companyId}`,
          customer: { id, email, phone },
        })
        return points
      }
    } catch (err) {
      message.error('Failed to get points')
    }
  }

  hide = () => {
    if (this.customer.changed) {
      if (window.confirm('Are you sure? You have unsaved changes.')) {
        this.props.onHide()
      }
    } else {
      this.props.onHide()
    }
  }

  fetchCustomer = async () => {
    const company = this.props.currentCompanyStore!.company!
    const { customerId, customerLocationId } = this.props

    if (isUndefined(customerId)) {
      return
    }

    try {
      const customerWithHistory = await API.getCustomerById(company.id, customerId)
      let points: number | undefined = 0
      if (this.canUseExternalLoyalty) {
        points = await this.loadExternalLoyaltyPoints(company.id, customerWithHistory.customer)
      }

      const customer = {
        ...customerWithHistory.customer,
        creditBalance:
          this.canUseExternalLoyalty && points !== undefined
            ? points
            : customerWithHistory.customer.creditBalance,
      }
      this.customer.populateFields(customer)

      this.setState({
        failedToLoadExternalLoyaltyPoints: points === undefined,
        customerWithHistory: {
          ...customerWithHistory,
          customer,
        },
      })

      if (customerLocationId) {
        this.customer.setLocationId(customerLocationId)
      }

      this.props.currentCompanyStore!.getLoyaltyTiers()
    } catch (error) {
      this.setState({
        errorMessage: parseErrorMsg(error) || 'There was an error fetching customer data.',
      })
    }
  }

  render() {
    const { customerId, canSaveCustomer, onSubmit, loadCustomers } = this.props
    const { customerWithHistory, payments, failedToLoadExternalLoyaltyPoints } = this.state
    const loyaltyTiers = this.props.currentCompanyStore!.loyaltyTiers || []

    let matchingLoyaltyTier: IBaseLoyaltyTier | undefined
    if (this.customer.loyaltyTierId) {
      matchingLoyaltyTier = loyaltyTiers.find(
        (loyaltyTier) => loyaltyTier.id === this.customer.loyaltyTierId,
      )
    }

    return (
      <Modal isOpen={true} toggle={this.hide} size={customerId ? 'xl' : 'lg'} autoFocus={false}>
        <ModalHeader toggle={this.hide}>
          <div className='text-center'>{customerId ? 'Customer details' : 'Add Customer'}</div>
        </ModalHeader>
        <ModalBody>
          <div className='customer-view-and-edit-modal'>
            <div className='customer-info-section'>
              <CustomerInfoForm
                canSaveCustomer={canSaveCustomer}
                customer={this.customer}
                onSubmit={onSubmit}
                onCustomerUndelete={this.props.onCustomerUndelete}
              />
            </div>
            {customerId && (
              <div className='customer-history-section'>
                {customerWithHistory && (
                  <CustomerHistorySection
                    canUseExternalLoyalty={this.canUseExternalLoyalty}
                    canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
                    customerWithHistory={customerWithHistory}
                    matchingLoyaltyTier={matchingLoyaltyTier}
                    fetchCustomer={this.fetchCustomer}
                    loadCustomers={loadCustomers}
                    failedToLoadExternalLoyaltyPoints={failedToLoadExternalLoyaltyPoints}
                  />
                )}
                <Collapse defaultActiveKey={[]} ghost>
                  <Collapse.Panel header='Receipts' key='1'>
                    {payments && <CustomerPayments payments={payments} />}
                  </Collapse.Panel>

                  {this.canAccessLoyalty && !this.canUseExternalLoyalty && (
                    <Collapse.Panel header='Loyalty' key='2'>
                      <CustomerLoyaltyLogs customerId={customerId} />
                    </Collapse.Panel>
                  )}
                </Collapse>
              </div>
            )}
          </div>
        </ModalBody>
      </Modal>
    )
  }
}
